import { useEffect, useState } from "react";
import {
  Button,
  Spinner,
  Toast,
  Toaster,
  useToastController,
  Text,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  Checkmark24Regular,
  ArrowClockwise24Regular,
} from "@fluentui/react-icons";
import AdminService from "./AdminService";
import { useAdminAuth } from "./AdminAuthContext";
import { formatDateTime } from "./adminUtil";

const useStyles = makeStyles({
  row: {
    display: "flex",
    flexDirection: "column",
    padding: "12px",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    marginBottom: "12px",
    backgroundColor: tokens.colorNeutralBackground2,
  },
  rowTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  metadata: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    marginTop: "10px",
  },
  meta: {
    fontSize: "12px",
    color: tokens.colorNeutralForeground3,
  },
});


export default function CallRecordSubscriptionButton() {
  const { userInfo } = useAdminAuth();
  const styles = useStyles();


  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [subscribedBy, setSubscribedBy] = useState(null);
  const [subscribedAt, setSubscribedAt] = useState(null);


  const toasterId = "subscription-toaster";
  const { dispatchToast } = useToastController(toasterId);


  // 🔹 Check existing subscription
  useEffect(() => {
    if (!userInfo?.tenantId) return;


    const checkSubscription = async () => {
      try {
        setChecking(true);


        const res = await AdminService.getCallRecordSubscription();
        console.log("res getCallRecordSubscription", res);


        const isCreated = res?.isCreated || false;
        const count = res?.count || 0;
        if (isCreated || Number(count) > 0) {
          setIsSubscribed(true);
        } else {
          setIsSubscribed(false);
        }
        setSubscribedBy(res?.subscribedBy || null);
        setSubscribedAt(res?.subscribedAt || null);
      } catch (err) {
        console.error("Subscription check failed:", err);
        setIsSubscribed(false);
        setSubscribedBy(null);
        setSubscribedAt(null);
      } finally {
        setChecking(false);
      }
    };


    checkSubscription();
  }, [userInfo]);


  // 🔹 Create subscription
  const handleSubscribe = async () => {
    try {
      setLoading(true);


      const res = await AdminService.postCallRecordSubscription(
        3, // expirationInDays
      );
      console.log("res postCallRecordSubscription", res);


      if (res?.message) {
        setIsSubscribed(true);
        setSubscribedBy(res?.subscribedBy || null);
        setSubscribedAt(res?.subscribedAt || null);


        dispatchToast(<Toast>Subscription created successfully ✅</Toast>, {
          intent: "success",
        });
      }
    } catch (err) {
      console.error("Subscription creation failed:", err);


      dispatchToast(<Toast>Failed to create subscription ❌</Toast>, {
        intent: "error",
      });
    } finally {
      setLoading(false);
    }
  };


  // 🔹 Refresh subscription status
  const handleRefresh = async () => {
    try {
      setLoading(true);
      // refresh call record API
      const res = await AdminService.refreshCallRecordSubscription();
      console.log("res refresh Call RecordSubscription", res);


      if (res?.message) {
        setIsSubscribed(true);
        setSubscribedBy(res?.subscribedBy || null);
        setSubscribedAt(res?.subscribedAt || null);


        dispatchToast(
          <Toast>Call Record Subscription refreshed successfully✅</Toast>,
          {
            intent: "success",
          },
        );
      }
    } catch (err) {
      console.error("Subscription creation failed:", err);


      dispatchToast(
        <Toast>Failed to Refresh Call Record Subscription❌</Toast>,
        {
          intent: "error",
        },
      );
    } finally {
      setLoading(false);
    }
  };


  return (
    <>
      <Toaster toasterId={toasterId} />


      <div className={styles.row}>
        <div className={styles.rowTop}>
          <Text>Unaric Voice for Teams Sync - Call Record Subscription</Text>


          {checking ? (
            <Spinner size="tiny" />
          ) : !isSubscribed ? (
            // 🔹 Subscribe Button
            <Button
              appearance="primary"
              onClick={handleSubscribe}
              disabled={loading}
              icon={<Checkmark24Regular />}
            >
              {loading ? "Creating..." : "Subscribe"}
            </Button>
          ) : (
            // 🔹 Refresh Button (shown only when subscribed)
            <Button
              appearance="secondary"
              onClick={handleRefresh}
              disabled={loading}
              icon={<ArrowClockwise24Regular />}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </Button>
          )}
        </div>

        {!checking && isSubscribed && (subscribedBy || subscribedAt) && (
          <div className={styles.metadata}>
            {subscribedBy && (
              <Text className={styles.meta}>
                Call Record Subscription by: <b>{subscribedBy}</b>
              </Text>
            )}
            {subscribedAt && (
              <Text className={styles.meta}>
                Call Record Subscription at (UTC):{" "}
                <b>{formatDateTime(subscribedAt)}</b>
              </Text>
            )}
          </div>
        )}
      </div>
    </>
  );
}
